import React, { useState, useRef } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Form, Button, InputGroup, Badge, Spinner, Alert, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');

const resolveUploadUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${UPLOAD_BASE}${path.startsWith('/') ? path : `/${path}`}`;
};

const genderLabel = (gen) => {
  if (gen === 'm') return <Badge bg="primary">ชาย</Badge>;
  return <Badge bg="danger">หญิง</Badge>;
};

const statusLabel = (status) => {
  if (status === '1' || status === '3') return <Badge bg="success">ปกติ</Badge>;
  return <Badge bg="secondary">ไม่ใช้งาน</Badge>;
};

// General class: "ม. 1/16 (2569)". Strip the "ชั้นมัธยมศึกษาปีที่" prefix, keep only the level number.
const generalClassLabel = (st) => {
  if (!st.c_class || !st.gen_room_no) return '-';
  const level = String(st.c_class).replace(/\D/g, '') || st.c_class;
  return `ม. ${level}/${st.gen_room_no}${st.gen_year ? ` (${st.gen_year})` : ''}`;
};

// Islamic class: "5 / A (AIP)". Program code shown only when it's not the General Program (GP).
const islamicClassLabel = (st) => {
  if (!st.ic_class) return '-';
  let label = `${st.ic_class}`;
  if (st.isl_room_no) label += ` / ${st.isl_room_no}`;
  if (st.is_programCode && st.is_programCode !== 'GP') label += ` (${st.is_programCode})`;
  return label;
};

const avatarInitial = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return parts[0][0] + parts[1][0];
  return parts[0][0];
};

export default function StudentSearch() {
  const currentSkin = (localStorage.getItem('skin-mode')) ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    if (skin === 'dark') {
      const btnWhite = document.getElementsByClassName('btn-white');
      for (const btn of btnWhite) {
        btn.classList.add('btn-outline-primary');
        btn.classList.remove('btn-white');
      }
    } else {
      const btnOutlinePrimary = document.getElementsByClassName('btn-outline-primary');
      for (const btn of btnOutlinePrimary) {
        btn.classList.remove('btn-outline-primary');
        btn.classList.add('btn-white');
      }
    }
  };

  switchSkin(skin);

  const [searchKey, setSearchKey] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const inputRef = useRef(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    const key = searchKey.trim();
    if (key.length < 2) {
      setError('กรุณากรอกคำค้นหาอย่างน้อย 2 ตัวอักษร');
      return;
    }
    setError('');
    setLoading(true);
    setSearched(false);
    setResults([]);
    try {
      const res = await axiosInstance.get(`/api/students/search?q=${encodeURIComponent(key)}`);
      if (res.data.success) {
        setResults(res.data.data);
        setSearched(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการค้นหา');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchKey('');
    setResults([]);
    setSearched(false);
    setError('');
    inputRef.current?.focus();
  };

  const handleOpenDetail = async (student) => {
    setShowDetail(true);
    setDetailLoading(true);
    setSelectedStudent(student);

    try {
      const res = await axiosInstance.get(`/api/getStudentInfo/${student.st_id}`);
      setSelectedStudent(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      setError(err.response?.data?.message || 'ไม่สามารถดึงข้อมูลนักเรียนได้');
    } finally {
      setDetailLoading(false);
    }
  };

  const detailValue = (value) => value || '-';

  const renderDetailItem = (label, value) => (
    <div className="col-12 col-md-6">
      <div className="text-muted fs-sm">{label}</div>
      <div className="fw-medium">{detailValue(value)}</div>
    </div>
  );

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ข้อมูลนักเรียน</Link></li>
              <li className="breadcrumb-item active" aria-current="page">ค้นหาข้อมูลนักเรียน</li>
            </ol>
            <h4 className="main-title mb-0">ค้นหาข้อมูลนักเรียน</h4>
          </div>
        </div>

        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        <Card className="card-one mb-4">
          <Card.Body>
            <Form onSubmit={handleSearch}>
              <Form.Label><strong>ค้นหานักเรียน</strong></Form.Label>
              <InputGroup>
                <Form.Control
                  ref={inputRef}
                  type="text"
                  value={searchKey}
                  onChange={e => setSearchKey(e.target.value)}
                  placeholder="ค้นหาด้วยเลขประจำตัวหรือชื่อ-สกุล..."
                  autoFocus
                />
                {searchKey && (
                  <Button variant="outline-secondary" onClick={handleClear} type="button">
                    ล้าง
                  </Button>
                )}
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" /> : <><i className="ri-search-2-line me-1"></i>ค้นหา</>}
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">ค้นหาจากเลขประจำตัวนักเรียน หรือชื่อ-สกุล</Form.Text>
            </Form>
          </Card.Body>
        </Card>

        {searched && results.length === 0 && (
          <Alert variant="warning">ไม่พบนักเรียนที่ค้นหา</Alert>
        )}

        {results.length > 0 && (
          <>
            <p className="text-muted fs-sm mb-3">พบ {results.length} รายการ{results.length === 30 ? ' (แสดงสูงสุด 30 รายการ)' : ''}</p>
            <Row className="g-3">
              {results.map((st) => {
                const initial = avatarInitial(st.st_name);
                const photoUrl = resolveUploadUrl(st.st_photo);

                const genClass = generalClassLabel(st);

                const islClass = islamicClassLabel(st);

                const dormInfo = st.dorm_name
                  ? `${st.village_name ? st.village_name + ' เลขที่ ' : ''}${st.dorm_name}`
                  : 'ยังไม่ได้ระบุหอพัก';

                return (
                  <Col xs={12} md={6} xl={4} key={st.st_id}>
                    <Card className="card-one h-100">
                      <Card.Body className="p-3">
                        <div className="d-flex gap-3 mb-3">
                          {photoUrl ? (
                            <img
                              src={photoUrl}
                              alt={st.st_name}
                              className="avatar avatar-lg rounded-circle flex-shrink-0"
                              style={{ width: 56, height: 56, objectFit: 'cover' }}
                            />
                          ) : (
                            <div className="avatar avatar-lg bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                 style={{ width: 56, height: 56, fontSize: 18, fontWeight: 600 }}>
                              {initial}
                            </div>
                          )}
                          <div className="flex-grow-1 overflow-hidden">
                            <div className="fw-semibold text-truncate">{st.st_name}</div>
                            <div className="text-muted fs-sm">{st.st_idnumber}</div>
                            <div className="d-flex gap-1 mt-1">
                              {genderLabel(st.st_gen)}
                              {statusLabel(st.st_status)}
                            </div>
                          </div>
                        </div>

                        <div className="row g-2 fs-sm">
                          <div className="col-5 text-muted">ชั้นสามัญ</div>
                          <div className="col-7 fw-medium">{genClass}</div>

                          <div className="col-5 text-muted">ชั้นศาสนา</div>
                          <div className="col-7 fw-medium">{islClass}</div>

                          <div className="col-5 text-muted">หอพัก</div>
                          <div className={`col-7 fw-medium${!st.dorm_name ? ' text-danger' : ''}`}>{dormInfo}</div>

                          {(st.st_parentname || st.st_parentPhone) && (
                            <>
                              <div className="col-5 text-muted">ผู้ปกครอง</div>
                              <div className="col-7">
                                <div>{st.st_parentname || '-'}</div>
                                {st.st_parentPhone && (
                                  <a href={`tel:${st.st_parentPhone}`} className="text-primary">
                                    <i className="ri-phone-line me-1"></i>{st.st_parentPhone}
                                  </a>
                                )}
                              </div>
                            </>
                          )}

                          {(st.st_fatherName || st.st_fatherPhone) && (
                            <>
                              <div className="col-5 text-muted">บิดา</div>
                              <div className="col-7">
                                <div>{st.st_fatherName || '-'}</div>
                                {st.st_fatherPhone && (
                                  <a href={`tel:${st.st_fatherPhone}`} className="text-primary">
                                    <i className="ri-phone-line me-1"></i>{st.st_fatherPhone}
                                  </a>
                                )}
                              </div>
                            </>
                          )}

                          {(st.st_motherName || st.st_motherPhone) && (
                            <>
                              <div className="col-5 text-muted">มารดา</div>
                              <div className="col-7">
                                <div>{st.st_motherName || '-'}</div>
                                {st.st_motherPhone && (
                                  <a href={`tel:${st.st_motherPhone}`} className="text-primary">
                                    <i className="ri-phone-line me-1"></i>{st.st_motherPhone}
                                  </a>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        <div className="d-grid mt-3">
                          <Button variant="outline-primary" size="sm" onClick={() => handleOpenDetail(st)}>
                            <i className="ri-contacts-book-2-line me-1"></i>แสดงข้อมูลพื้นฐาน
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </>
        )}

        <Modal show={showDetail} onHide={() => setShowDetail(false)} size="lg" centered scrollable>
          <Modal.Header closeButton>
            <Modal.Title as="h6">
              <i className="ri-user-search-line me-1"></i>ข้อมูลพื้นฐานนักเรียน
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {detailLoading && !selectedStudent ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" /> กำลังโหลดข้อมูล...
              </div>
            ) : selectedStudent && (
              <>
                <div className="d-flex flex-column flex-md-row gap-3 mb-4">
                  <div
                    className="border bg-light d-flex align-items-center justify-content-center overflow-hidden flex-shrink-0"
                    style={{ width: 'min(100%, 150px)', aspectRatio: '3 / 4', borderRadius: 8 }}
                  >
                    {resolveUploadUrl(selectedStudent.st_photo) ? (
                      <img
                        src={resolveUploadUrl(selectedStudent.st_photo)}
                        alt={selectedStudent.st_name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div className="avatar avatar-lg bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                           style={{ width: 64, height: 64, fontSize: 20, fontWeight: 600 }}>
                        {avatarInitial(selectedStudent.st_name)}
                      </div>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <h5 className="mb-1">{selectedStudent.st_name}</h5>
                    <div className="text-muted mb-2">{selectedStudent.st_idnumber}</div>
                    <div className="d-flex flex-wrap gap-1">
                      {genderLabel(selectedStudent.st_gen)}
                      {statusLabel(selectedStudent.st_status)}
                      {selectedStudent.c_class && selectedStudent.gen_room_no && (
                        <Badge bg="info">{generalClassLabel(selectedStudent)}</Badge>
                      )}
                    </div>
                  </div>
                </div>

                <h6 className="border-bottom pb-2 mb-3">ข้อมูลส่วนตัว</h6>
                <Row className="g-3 mb-4">
                  {renderDetailItem('เลขประจำตัว', selectedStudent.st_idnumber)}
                  {renderDetailItem('เลขประจำตัวประชาชน', selectedStudent.st_idcard)}
                  {renderDetailItem('วันเกิด', selectedStudent.st_birthdate)}
                  {renderDetailItem('ศาสนา', selectedStudent.st_religion)}
                  {renderDetailItem('สัญชาติ', selectedStudent.st_nationality)}
                  {renderDetailItem('เชื้อชาติ', selectedStudent.st_race)}
                  {renderDetailItem('หมู่เลือด', selectedStudent.st_blood)}
                  {renderDetailItem('ชั้นสามัญ', generalClassLabel(selectedStudent))}
                  {renderDetailItem('ชั้นศาสนา', islamicClassLabel(selectedStudent))}
                  {renderDetailItem('หอพัก', selectedStudent.dorm_name ? `${selectedStudent.village_name ? selectedStudent.village_name + ' เลขที่ ' : ''}${selectedStudent.dorm_name}` : '-')}
                </Row>

                <h6 className="border-bottom pb-2 mb-3">ที่อยู่</h6>
                <Row className="g-3 mb-4">
                  {renderDetailItem('ที่อยู่ปัจจุบัน', selectedStudent.st_current)}
                  {renderDetailItem('ตำบล', selectedStudent.st_currentSubDistrict)}
                  {renderDetailItem('อำเภอ', selectedStudent.st_currentDistrict)}
                  {renderDetailItem('จังหวัด', selectedStudent.st_currentProvince)}
                  {renderDetailItem('รหัสไปรษณีย์', selectedStudent.st_zipcode)}
                  {renderDetailItem('ภูมิลำเนาเดิม', selectedStudent.st_oldaddress)}
                </Row>

                <h6 className="border-bottom pb-2 mb-3">ผู้ปกครองและครอบครัว</h6>
                <Row className="g-3">
                  {renderDetailItem('บิดา', selectedStudent.st_fatherName)}
                  {renderDetailItem('โทรศัพท์บิดา', selectedStudent.st_fatherPhone)}
                  {renderDetailItem('มารดา', selectedStudent.st_motherName)}
                  {renderDetailItem('โทรศัพท์มารดา', selectedStudent.st_motherPhone)}
                  {renderDetailItem('ผู้ปกครอง', selectedStudent.st_parentname)}
                  {renderDetailItem('เกี่ยวข้องเป็น', selectedStudent.st_parentRelation)}
                  {renderDetailItem('โทรศัพท์ผู้ปกครอง', selectedStudent.st_parentPhone)}
                  {renderDetailItem('อาชีพผู้ปกครอง', selectedStudent.st_parentjob)}
                </Row>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowDetail(false)}>
              <i className="ri-close-line me-1"></i>ปิด
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}
